import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useInventory } from '../../hooks/useInventory';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useProducts } from '../../hooks/useProducts';
import { Inventory, InventoryQueryParams } from '../../types/warehouse';
import { Product } from '../../types/product';
import LoadingScreen from '../common/LoadingScreen';
import Pagination from '../common/Pagination';
import ProductView from '../products/ProductView';
import StockTransferForm from './StockTransferForm';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  BuildingStorefrontIcon,
  ClipboardIcon,
  EyeIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';

interface InventoryListProps {
  productId?: string;
  warehouseId?: string;
  onSelect?: (inventory: Inventory) => void;
}

// Termékkód másolása a vágólapra
const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
    .then(() => {
      toast.success('Kód másolva a vágólapra!', {
        duration: 2000,
        position: 'bottom-center'
      });
    })
    .catch(err => {
      console.error('Nem sikerült másolni:', err);
      toast.error('Nem sikerült másolni a kódot', {
        duration: 2000,
        position: 'bottom-center'
      });
    });
};

const InventoryList: React.FC<InventoryListProps> = ({
  productId,
  warehouseId,
  onSelect
}) => {
  const { t } = useLanguage();
  const { inventory, loading, error, fetchInventory } = useInventory();
  const { warehouses, loading: loadingWarehouses } = useWarehouses();
  const { products } = useProducts();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState(warehouseId || '');
  const [filterParams, setFilterParams] = useState<{
    belowMinimum: boolean;
    aboveMaximum: boolean;
    onlyAvailable: boolean;
  }>({
    belowMinimum: false,
    aboveMaximum: false,
    onlyAvailable: false
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ProductView modal state
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isProductViewOpen, setIsProductViewOpen] = useState(false);

  // Stock Transfer modal state
  const [isStockTransferOpen, setIsStockTransferOpen] = useState(false);
  const [selectedInventoryForTransfer, setSelectedInventoryForTransfer] = useState<Inventory | null>(null);

  // Készlet betöltése
  useEffect(() => {
    const loadInventory = async () => {
      const params: InventoryQueryParams = {
        ...filterParams,
        searchTerm: searchTerm.length > 2 ? searchTerm : undefined
      };
      
      if (productId) {
        params.productId = productId;
      }
      
      if (selectedWarehouse) {
        params.warehouseId = selectedWarehouse;
      }
      
      await fetchInventory(params);
    };
    
    loadInventory();
  }, [fetchInventory, productId, selectedWarehouse, searchTerm, filterParams]);

  // Szűrő változások kezelése
  const handleFilterChange = (filterName: keyof typeof filterParams) => {
    setFilterParams(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  // Keresés kezelése
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Frissítés kezelése
  const handleRefresh = () => {
    fetchInventory({
      productId,
      warehouseId: selectedWarehouse || undefined,
      ...filterParams,
      searchTerm: searchTerm.length > 2 ? searchTerm : undefined
    });
  };

  // Raktár változás kezelése
  const handleWarehouseChange = (warehouseId: string) => {
    setSelectedWarehouse(warehouseId);
  };

  // Filter inventory based on search term
  const filteredInventory = useMemo(() => {
    return inventory.filter(item => {
      // Filter by search term
      const searchMatch = searchTerm.length < 3 || 
        item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.productCode && item.productCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.warehouseName && item.warehouseName.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Apply filters
      const belowMinimumMatch = !filterParams.belowMinimum || 
        (item.minQuantity !== undefined && item.quantity < item.minQuantity);
      
      const onlyAvailableMatch = !filterParams.onlyAvailable || 
        ((item.quantity || 0) - (item.reservedQuantity || 0) > 0);
      
      return searchMatch && belowMinimumMatch && onlyAvailableMatch;
    });
  }, [inventory, searchTerm, filterParams]);

  // Pagination logic
  const totalPages = Math.ceil(filteredInventory.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInventory = filteredInventory.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Termék megtekintése
  const handleProductClick = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setIsProductViewOpen(true);
    }
  };

  // Készlet állapot badge renderelése
  const renderStockStatusBadge = (item: Inventory) => {
    const { quantity, minQuantity, maxQuantity } = item;
    
    if (minQuantity !== undefined && quantity < minQuantity) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-700 shadow-sm">
          <ExclamationTriangleIcon className="w-3 h-3 mr-1.5 text-red-600 dark:text-red-400" />
          {t('inventory.belowMinimum')}
        </span>
      );
    }
    
    if (maxQuantity !== undefined && quantity > maxQuantity) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-700 shadow-sm">
          <ExclamationTriangleIcon className="w-3 h-3 mr-1.5 text-yellow-600 dark:text-yellow-400" />
          {t('inventory.aboveMaximum')}
        </span>
      );
    }
    
    if (quantity === 0) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600 shadow-sm">
          <XCircleIcon className="w-3 h-3 mr-1.5 text-gray-500 dark:text-gray-400" />
          {t('inventory.outOfStock')}
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-700 shadow-sm">
        <CheckCircleIcon className="w-3 h-3 mr-1.5 text-green-600 dark:text-green-400" />
        {t('inventory.inStock')}
      </span>
    );
  };

  // Handle stock transfer
  const handleStockTransfer = (inventoryItem: Inventory) => {
    setSelectedInventoryForTransfer(inventoryItem);
    setIsStockTransferOpen(true);
  };

  const handleTransferSuccess = () => {
    fetchInventory({
      productId,
      warehouseId: selectedWarehouse || undefined,
      ...filterParams,
      searchTerm: searchTerm.length > 2 ? searchTerm : undefined
    });
  };

  return (
    <div className="space-y-4 w-full max-w-full flex-grow">
      {/* Raktárválasztó csempék */}
      {!warehouseId && !loadingWarehouses && warehouses.length > 0 && (
        <div className="w-full mb-4">
          <div className="flex flex-wrap gap-2 mt-2">
            <button
              onClick={() => handleWarehouseChange("")}
              className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                selectedWarehouse === ""
                  ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500"
              }`}
            >
              <BuildingStorefrontIcon className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{t('inventory.allWarehouses')}</span>
              <span className="sm:hidden">Összes</span>
            </button>
            
            {warehouses.map(warehouse => (
              <button
                key={warehouse.id}
                onClick={() => handleWarehouseChange(warehouse.id)}
                className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedWarehouse === warehouse.id
                    ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700"
                    : warehouse.isDefault
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500"
                }`}
              >
                <BuildingStorefrontIcon className="h-4 w-4 mr-2" />
                <span className="truncate max-w-[80px] sm:max-w-none">{warehouse.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Szűrők és keresés - mobil optimalizált */}
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        {/* Szűrő gombok */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => handleFilterChange('belowMinimum')}
            className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium transition-colors ${
              filterParams.belowMinimum
                ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400 border-red-200 dark:border-red-700 hover:bg-red-200 dark:hover:bg-red-900/40'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <ExclamationTriangleIcon className={`h-4 w-4 mr-1.5 ${filterParams.belowMinimum ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'}`} />
            <span className="hidden sm:inline">{t('inventory.belowMinimum')}</span>
            <span className="sm:hidden">Min alatt</span>
          </button>
          
          <button
            type="button"
            onClick={() => handleFilterChange('onlyAvailable')}
            className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium transition-colors ${
              filterParams.onlyAvailable
                ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 border-green-200 dark:border-green-700 hover:bg-green-200 dark:hover:bg-green-900/40'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <CheckCircleIcon className={`h-4 w-4 mr-1.5 ${filterParams.onlyAvailable ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`} />
            <span className="hidden sm:inline">{t('inventory.onlyAvailable')}</span>
            <span className="sm:hidden">Elérhető</span>
          </button>
          
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center justify-center px-2.5 py-1 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            title={t('common.refresh')}
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Keresés */}
        <div className="flex-1 max-w-full lg:max-w-md">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            </div>
            <input
              type="text"
              name="search"
              id="search"
              className="block w-full rounded-md border-2 border-gray-300 dark:border-gray-600 pl-8 py-2 sm:py-1.5 px-3 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-md w-full">
          {error}
        </div>
      ) : inventory.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 text-yellow-700 dark:text-yellow-400 px-4 py-3 rounded-md w-full">
          {t('inventory.noItemsFound')}
        </div>
      ) : (
        <>
          {/* Desktop táblázat */}
          <div className="hidden lg:block overflow-hidden bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 w-full">
            <div className="overflow-x-auto w-full">
              <table className="table-fixed min-w-full w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {!productId && (
                      <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[30%]">
                        {t('inventory.product')}
                      </th>
                    )}
                    <th scope="col" className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[140px]">
                      {t('inventory.status')}
                    </th>
                    {!warehouseId && !selectedWarehouse && (
                      <th scope="col" className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[150px]">
                        {t('inventory.warehouse')}
                      </th>
                    )}
                    <th scope="col" className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[140px]">
                      {t('inventory.quantity')}
                    </th>
                    {/* Műveletek oszlop fejléc - mindig megjelenik */}
                    <th scope="col" className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[100px]">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {currentInventory.map((item) => (
                    <tr 
                      key={item.id} 
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${onSelect ? 'cursor-pointer' : ''}`}
                      onClick={() => onSelect && onSelect(item)}
                    >
                      {!productId && (
                        <td className="px-2 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                          <div className="flex flex-col">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProductClick(item.productId);
                              }}
                              className="text-left hover:text-primary-600 dark:hover:text-primary-400 hover:underline transition-colors cursor-pointer"
                            >
                              {item.productName}
                            </button>
                            <div 
                              className="text-xs text-gray-500 dark:text-gray-400 flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-primary-600 dark:hover:text-primary-400 px-1 py-0.5 rounded transition-colors w-fit group"
                              onClick={(e) => {
                                e.stopPropagation(); 
                                copyToClipboard(item.productCode);
                              }}
                              title="Kattints a kód másolásához"
                            >
                              <ClipboardIcon className="h-3 w-3 mr-1 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                              <span>{item.productCode}</span>
                            </div>
                          </div>
                        </td>
                      )}
                      <td className="px-2 py-1.5 whitespace-nowrap text-sm text-center">
                        {renderStockStatusBadge(item)}
                      </td>
                      {!warehouseId && !selectedWarehouse && (
                        <td className="px-2 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {item.warehouseName}
                        </td>
                      )}
                      <td className="px-2 py-1.5 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-gray-100">
                        <div className="flex flex-col items-end">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {item.quantity} {item.unit}
                          </div>
                          {(item.reservedQuantity || 0) > 0 && (
                            <div className="text-xs text-red-600 dark:text-red-400">
                              ({item.reservedQuantity} foglalt)
                            </div>
                          )}
                          <div className={`text-xs font-medium ${
                            ((item.quantity || 0) - (item.reservedQuantity || 0)) > 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {Math.max(0, (item.quantity || 0) - (item.reservedQuantity || 0))} elérhető
                          </div>
                        </div>
                      </td>
                      {/* Műveletek cellák - mindig megjelenik */}
                      <td className="px-2 py-1.5 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-1">
                          {!productId && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProductClick(item.productId);
                              }}
                              className="text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 p-1 rounded-full hover:bg-primary-50 dark:hover:bg-primary-900/20"
                              title="Termék megtekintése"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Átmozgatás gomb - mindig megjelenik */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStockTransfer(item);
                            }}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 p-1 rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            title="Átmozgatás másik raktárba"
                          >
                            <ArrowsRightLeftIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobil kártyák */}
          <div className="lg:hidden space-y-4">
            {currentInventory.map((item) => (
              <div
                key={item.id}
                className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden ${
                  onSelect ? 'cursor-pointer' : ''
                }`}
                onClick={() => onSelect && onSelect(item)}
              >
                {/* Kártya fejléc */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                  {!productId && (
                    <div className="mb-3">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {item.productName}
                      </h3>
                      
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium mr-2">Kód:</span>
                        <div 
                          className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs font-mono flex items-center cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-700 dark:hover:text-primary-400 transition-colors group"
                          onClick={(e) => {
                            e.stopPropagation(); 
                            copyToClipboard(item.productCode);
                          }}
                          title="Kattints a kód másolásához"
                        >
                          <ClipboardIcon className="h-3 w-3 mr-1 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                          <span>{item.productCode}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {!warehouseId && !selectedWarehouse && (
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-3">
                      <BuildingStorefrontIcon className="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                      <span className="font-medium mr-2">Raktár:</span>
                      <span>{item.warehouseName}</span>
                    </div>
                  )}

                  {/* Állapot */}
                  <div className="mb-3">
                    {renderStockStatusBadge(item)}
                  </div>

                  {/* Mennyiségek */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Készlet:</span>
                      <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {item.quantity} {item.unit}
                      </span>
                    </div>
                    
                    {(item.reservedQuantity || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Foglalt:</span>
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">
                          {item.reservedQuantity} {item.unit}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Elérhető:</span>
                      <span className={`text-sm font-semibold ${
                        ((item.quantity || 0) - (item.reservedQuantity || 0)) > 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {Math.max(0, (item.quantity || 0) - (item.reservedQuantity || 0))} {item.unit}
                      </span>
                    </div>

                    {/* Min/Max mennyiségek */}
                    {(item.minQuantity !== undefined || item.maxQuantity !== undefined) && (
                      <div className="pt-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                        {item.minQuantity !== undefined && (
                          <div className="flex justify-between">
                            <span>Min szint:</span>
                            <span>{item.minQuantity} {item.unit}</span>
                          </div>
                        )}
                        {item.maxQuantity !== undefined && (
                          <div className="flex justify-between">
                            <span>Max szint:</span>
                            <span>{item.maxQuantity} {item.unit}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons - mobil kártyák */}
                <div className="flex border-t border-gray-200 dark:border-gray-700">
                  {!productId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductClick(item.productId);
                      }}
                      className="flex-1 py-3 px-4 text-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                      title="Termék megtekintése"
                    >
                      <div className="flex items-center justify-center">
                        <EyeIcon className="h-5 w-5 mr-2" />
                        <span className="text-sm">Megtekintés</span>
                      </div>
                    </button>
                  )}
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStockTransfer(item);
                    }}
                    className="flex-1 py-3 px-4 text-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title="Átmozgatás másik raktárba"
                  >
                    <div className="flex items-center justify-center">
                      <ArrowsRightLeftIcon className="h-5 w-5 mr-2" />
                      <span className="text-sm">Átmozgatás</span>
                    </div>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      
      {filteredInventory.length > 0 && (
        <div className="relative z-10">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredInventory.length}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}

      {selectedProduct && (
        <ProductView
          product={selectedProduct}
          isOpen={isProductViewOpen}
          onClose={() => {
            setIsProductViewOpen(false);
            setSelectedProduct(null);
          }}
        />
      )}

      {selectedInventoryForTransfer && (
        <StockTransferForm
          isOpen={isStockTransferOpen}
          onClose={() => {
            setIsStockTransferOpen(false);
            setSelectedInventoryForTransfer(null);
          }}
          onSuccess={handleTransferSuccess}
          preSelectedProductId={selectedInventoryForTransfer.productId}
          preSelectedFromWarehouseId={selectedInventoryForTransfer.warehouseId}
        />
      )}
    </div>
  );
};

export default InventoryList; 